import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from '../../axiosConfig';
import '../../styles/RegisterSchool.css';

const RegisterSchool = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { trainerData, schoolData } = location.state || {};

    // Restore school data if available, otherwise use empty form
    const [formData, setFormData] = useState(
        schoolData || {
            name: '',
            country: '',
            city: '',
            postalCode: '',
            street: '',
            buildingNumber: '',
            apartmentNumber: '',
            phone: '',
            email: '',
        }
    );
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!trainerData) {
            // Jeśli dane trenera nie są dostępne, wracamy do rejestracji trenera
            navigate('/register-trainer');
        }
    }, [trainerData, navigate]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            // 1️⃣ Budujemy request zgodny z RegisterTrainerRequest
            const trainerRequest = {
                username: trainerData.username,
                password: trainerData.password,
                firstName: trainerData.firstName,
                lastName: trainerData.lastName,
                email: trainerData.email,
                phone: trainerData.phone,
                statuteAccepted: trainerData.statuteAccepted,
                address: {
                    country: trainerData.country,
                    city: trainerData.city,
                    postalCode: trainerData.postalCode,
                    street: trainerData.street,
                    buildingNumber: trainerData.buildingNumber,
                    apartmentNumber: trainerData.apartmentNumber,
                }
            };

            // Rejestrujemy trenera
            const trainerResponse = await axios.post('/api/v1/auth/register/trainer', trainerRequest);
            const trainerId = trainerResponse.data; // UUID zwrócony przez backend

            // 2️⃣ Budujemy request dla szkoły (CreateSchoolRequest)
            const schoolRequest = {
                name: formData.name,
                address: {
                    country: formData.country,
                    city: formData.city,
                    postalCode: formData.postalCode,
                    street: formData.street,
                    buildingNumber: formData.buildingNumber,
                    apartmentNumber: formData.apartmentNumber,
                },
                phone: formData.phone,
                email: formData.email,
            };

            // Tworzymy szkołę (endpoint z {trainerId} w URL)
            await axios.post(`/api/v1/school/create/${trainerId}`, schoolRequest);

            // 3️⃣ Przekierowanie do strony sukcesu
            navigate('/registration-success');
        } catch (err) {
            console.error('Registration error:', err);
            setError('Coś poszło nie tak. Spróbuj ponownie.');
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        // Pass both trainer data and current school data back
        navigate('/register-trainer', {
            state: {
                trainerData,
                schoolData: formData
            }
        });
    };

    return (
        <div className="school-registration">
            <div className="school-registration__left-panel">
                <div className="school-registration__left-content">
                    <h2 className="school-registration__welcome-text">
                        Dziękujemy, że
                        <span className="school-registration__welcome-text--highlight">nas wybrałeś</span>
                    </h2>
                </div>
            </div>
            <div className="school-registration__right-panel">
                <button className="school-registration__back-btn" onClick={handleBack}>
                    ← wróć
                </button>
                <div className="school-registration__form-wrapper">
                    <h1 className="school-registration__title">Utwórz szkołę</h1>
                    <form className="school-registration__form" onSubmit={handleSubmit}>
                        <div className="school-registration__form-row">
                            <div className="school-registration__field-group">
                                <input
                                    type="text"
                                    name="name"
                                    placeholder="Nazwa szkoły"
                                    className="school-registration__input"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div className="school-registration__field-group">
                                <input
                                    type="text"
                                    name="country"
                                    placeholder="Kraj"
                                    className="school-registration__input"
                                    value={formData.country}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        <div className="school-registration__form-row">
                            <div className="school-registration__field-group">
                                <input
                                    type="text"
                                    name="city"
                                    placeholder="Miasto"
                                    className="school-registration__input"
                                    value={formData.city}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div className="school-registration__field-group">
                                <input
                                    type="text"
                                    name="postalCode"
                                    placeholder="Kod pocztowy"
                                    className="school-registration__input"
                                    value={formData.postalCode}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        <div className="school-registration__form-row">
                            <div className="school-registration__field-group">
                                <input
                                    type="text"
                                    name="street"
                                    placeholder="Ulica"
                                    className="school-registration__input"
                                    value={formData.street}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div className="school-registration__field-group">
                                <input
                                    type="text"
                                    name="buildingNumber"
                                    placeholder="Numer budynku"
                                    className="school-registration__input"
                                    value={formData.buildingNumber}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        <div className="school-registration__form-row">
                            <div className="school-registration__field-group">
                                <input
                                    type="text"
                                    name="apartmentNumber"
                                    placeholder="Numer mieszkania"
                                    className="school-registration__input"
                                    value={formData.apartmentNumber}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="school-registration__field-group">
                                <input
                                    type="text"
                                    name="phone"
                                    placeholder="Numer telefonu"
                                    className="school-registration__input"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        <div className="school-registration__form-row">
                            <div className="school-registration__field-group">
                                <input
                                    type="email"
                                    name="email"
                                    placeholder="E-mail szkoły"
                                    className="school-registration__input"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        {error && <div className="school-registration__error">{error}</div>}

                        <button type="submit" className="school-registration__submit-btn" disabled={loading}>
                            {loading ? 'Rejestrowanie...' : 'Zarejestruj się'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default RegisterSchool;